/**
 * Unified nav visibility helper.
 *
 * Supports:
 *  - exact path matches
 *  - "param patterns" like `/admin/forms/:id`
 *  - raw RegExp rules
 *  - custom predicate functions
 *  - optional basename stripping (e.g., '/ECMv6')
 *
 * Usage:
 *   const { hideHeader, hideFooter } = getNavVisibility(location.pathname, NAV_VIS_CONFIG);
 *   if (hideHeader) return null;
 */

const toArray = (x) => (Array.isArray(x) ? x : x ? [x] : []);

const stripBase = (pathname = '', base = '') => {
  if (!base) return pathname;
  try {
    return pathname.startsWith(base) ? pathname.slice(base.length) || '/' : pathname;
  } catch {
    return pathname;
  }
};

/**
 * Convert a simple express-style pattern to a RegExp
 * Examples:
 *   '/admin/forms/:id' -> /^\/admin\/forms\/[^/]+\/?$/i
 *   '/admin/:tab/:sub?'           -> supports optional trailing param via '?'
 */
const paramPatternToRegex = (pattern) => {
  // Escape regex special chars except ':' and '?'
  const escaped = pattern
    .replace(/([.+^$()[\]{}|\\])/g, '\\$1')
    // Replace '/:param?' (optional) with '/([^/]+)?'
    .replace(/\/:([A-Za-z0-9_]+)\?/g, '/([^/]+)?')
    // Replace '/:param' with '/([^/]+)'
    .replace(/\/:([A-Za-z0-9_]+)/g, '/([^/]+)');

  return new RegExp(`^${escaped}\\/?$`, 'i');
};

const matchExact = (pathname, list) => toArray(list).some((p) => p === pathname);

const matchParamPatterns = (pathname, patterns) =>
  toArray(patterns).some((p) => paramPatternToRegex(p).test(pathname));

const matchRegexes = (pathname, regexes) =>
  toArray(regexes).some((re) => {
    try {
      return re instanceof RegExp ? re.test(pathname) : false;
    } catch {
      return false;
    }
  });

const matchCustom = (pathname, fns) =>
  toArray(fns).some((fn) => {
    try {
      return Boolean(fn?.(pathname));
    } catch {
      return false;
    }
  });

const isHidden = (pathname, rules = {}) => {
  const { exact, paramPatterns, regexes, custom } = rules;
  if (matchExact(pathname, exact)) return true;
  if (matchParamPatterns(pathname, paramPatterns)) return true;
  if (matchRegexes(pathname, regexes)) return true;
  if (matchCustom(pathname, custom)) return true;
  return false;
};

/**
 * Primary API
 * @param {string} rawPathname - e.g., '/ECMv6/admin/forms/123'
 * @param {object} config
 * @param {string} [config.base] - router basename to strip (e.g., '/ECMv6')
 * @param {object} [config.header] - rule sets for header visibility
 * @param {object} [config.footer] - rule sets for footer visibility
 * @returns {{ hideHeader: boolean, hideFooter: boolean }}
 */
export const getNavVisibility = (rawPathname, config = {}) => {
  const base = config.base || '';
  const pathname = stripBase(rawPathname, base);

  const hideHeader = isHidden(pathname, config.header);
  const hideFooter = isHidden(pathname, config.footer);

  return { hideHeader, hideFooter };
};
